import { StatusDotIcon } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/StatusDotIcon/StatusDotIcon';
import { ExpandedListContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/ExpandedListContainer.tsx';
import { StatusTooltipCount } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/StatusTooltipCount.tsx';
import { TextStatusRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/TextStatusRow.tsx';
import { DocumentStatusCell } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/DocumentStatusCell';
import { DocumentStatusFooter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/DocumentStatusFooter';
import {
	DocumentStatusBadge,
	KycOverallStatusBadge,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/KYCStatusBadge';
import {
	DOCUMENT_STATUS_FILTER_OPTIONS,
	getFileDisplayStatus,
	getInvestmentDisplayType,
	getInvestmentDisplayTypeBadgeColor,
	getInvestmentDisplayTypeColor,
	getInvestmentDisplayTypeLabel,
	getInvestmentTypeCount,
	getKycStatusCount,
	getKycStatusDotColor,
	getKycStatusLabel,
	getUniqueInvestorCount,
	groupFilesByDisplayStatus,
	INVESTMENT_DISPLAY_TYPE_ORDER,
	INVESTMENT_TYPE_FILTER_OPTIONS,
	InvestmentDisplayType,
	KYC_STATUS_FILTER_OPTIONS,
	KYC_STATUS_ORDER,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/utils';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { createCohortsColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/CohortsColumn.tsx';
import { TableRowActionsMenu } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { GetKYCWorkflowRowRes } from '@dealstack/shared/lib/generated/models/GetKYCWorkflowRowRes';
import { Flex, HoverCard, Text, Tooltip } from '@mantine/core';
import { TableRowActionOption } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu.types';

interface CreateKYCWorkflowsColumnsOptions {
	workflows: GetKYCWorkflowRowRes[];
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	mipClassifierFilterOptions: { label: string; value: string }[];
	onWithdrawStandalone?: (kycWorkflowId: string) => void;
	onKycClick?: (kycWorkflowId: string) => void;
	onInvestorDataClick?: (kycWorkflowId: string) => void;
	onTaxComplianceClick?: (kycWorkflowId: string) => void;
	onInvestmentClick?: (investorName: string) => void;
}

export const createKYCWorkflowsColumns = ({
	workflows,
	tMipManage,
	mipClassifierFilterOptions,
	onWithdrawStandalone,
	onKycClick,
	onInvestorDataClick,
	onTaxComplianceClick,
	onInvestmentClick,
}: CreateKYCWorkflowsColumnsOptions) => [
	{
		id: 'actions',
		header: '',
		minSize: 36,
		size: 36,
		meta: { isGrow: false },
		cell: ({ row }) => (
			<Flex h="100%" w="100%" align="flex-start" justify="center" px="12px">
				<Flex
					h="42px"
					align="center"
					justify="center"
					style={{ cursor: 'pointer' }}
					onClick={(e) => {
						e.stopPropagation();
						row.toggleExpanded();
					}}
				>
					<Icon
						variant="caret-right-new"
						size="xl"
						style={{
							transform: row.getIsExpanded() ? 'rotate(90deg)' : 'rotate(0deg)',
							transition: 'transform 0.2s ease-in-out',
						}}
					/>
				</Flex>
			</Flex>
		),
	},
	// Hidden by default: deep links (the investor details quick links) filter by it, since the
	// investor column is a free-text search.
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'investorId',
		headerText: tMipManage('kyc.table.columns.investor_id'),
		columnLabel: tMipManage('kyc.table.columns.investor_id'),
		accessorFn: (row) => row.investorId,
		enableSorting: false,
		enableGlobalFilter: false,
		showFilter: true,
		filterText: tMipManage('kyc.table.columns.investor_id'),
		filterVariant: 'text',
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'investor',
		headerText: tMipManage('kyc.table.columns.investor'),
		columnLabel: tMipManage('kyc.table.columns.investor'),
		accessorFn: (row) => `${row.investor.firstName} ${row.investor.lastName}`,
		size: 200,
		enableSorting: true,
		enableGlobalFilter: true,
		showFilter: true,
		filterText: tMipManage('kyc.table.filter.investor'),
		filterVariant: 'text',
		cell: ({ row }) => (
			<Flex direction="column" gap="0.25rem" w="100%">
				<Text size="sm" fw={500} truncate lineClamp={1}>
					{`${row.original.investor.firstName} ${row.original.investor.lastName}`}
				</Text>
				<Text size="sm" c="dark.0" truncate lineClamp={1}>
					{row.original.investor.email || LONG_DASH}
				</Text>
			</Flex>
		),
		showFooter: true,
		footer: () => (
			<Flex direction="column" justify="space-between" h="100%" w="100%">
				<Text size="xs" c="dark.0">
					{tMipManage('kyc.table.footer.investors')}
				</Text>
				<Text size="sm" fw={600}>
					{getUniqueInvestorCount(workflows)}
				</Text>
			</Flex>
		),
	}),
	createCohortsColumn<GetKYCWorkflowRowRes>({
		id: 'cohorts',
		headerText: tMipManage('investors.table.header.cohorts'),
		filterOptions: mipClassifierFilterOptions,
		accessorFn: (row) => row.cohorts.map((c) => c.name).join(', '),
		enableSorting: true,
		enableGlobalFilter: true,
		getCohorts: (row) => row.cohorts ?? [],
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'declarationsStatus',
		headerText: tMipManage('kyc.table.columns.declarations'),
		columnLabel: tMipManage('kyc.table.columns.declarations'),
		accessorFn: (row) => row.declarationsStatus,
		minSize: 200,
		enableSorting: false,
		enableGlobalFilter: false,
		showFilter: true,
		filterText: 'Filter',
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: DOCUMENT_STATUS_FILTER_OPTIONS },
		cell: ({ row }) => {
			const items = row.original.declarationItems ?? [];
			if (row.original.declarationsStatus === null && items.length === 0) {
				return <>{LONG_DASH}</>;
			}
			if (row.getIsExpanded()) {
				return (
					<ExpandedListContainer>
						{items.map((item) => (
							<TextStatusRow
								key={item.id}
								name={item.fileName}
								status={getFileDisplayStatus(item)}
								isInherited={item.isInherited}
								isKyc
							/>
						))}
					</ExpandedListContainer>
				);
			}
			return items.length ? (
				<Flex gap="0.5rem" wrap="wrap">
					{groupFilesByDisplayStatus(items).map((group) => (
						<StatusTooltipCount
							key={group.status}
							status={group.status}
							items={group.items}
							isKyc
						/>
					))}
				</Flex>
			) : (
				<DocumentStatusBadge status={row.original.declarationsStatus} />
			);
		},
		showFooter: true,
		footer: () => <DocumentStatusFooter workflows={workflows} field="declarationsStatus" />,
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'proofOfIdentityStatus',
		headerText: tMipManage('kyc.table.columns.proof_of_identity'),
		columnLabel: tMipManage('kyc.table.columns.proof_of_identity'),
		accessorFn: (row) => row.proofOfIdentityStatus,
		minSize: 200,
		enableSorting: false,
		enableGlobalFilter: false,
		showFilter: true,
		filterText: 'Filter',
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: DOCUMENT_STATUS_FILTER_OPTIONS },
		cell: ({ row }) => (
			<DocumentStatusCell
				files={row.original.poiFiles ?? []}
				overallStatus={row.original.proofOfIdentityStatus}
				isExpanded={row.getIsExpanded()}
				fallbackName={tMipManage('kyc.table.columns.proof_of_identity')}
			/>
		),
		showFooter: true,
		footer: () => <DocumentStatusFooter workflows={workflows} field="proofOfIdentityStatus" />,
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'proofOfAddressStatus',
		headerText: tMipManage('kyc.table.columns.proof_of_address'),
		columnLabel: tMipManage('kyc.table.columns.proof_of_address'),
		accessorFn: (row) => row.proofOfAddressStatus,
		minSize: 200,
		enableSorting: false,
		enableGlobalFilter: false,
		showFilter: true,
		filterText: 'Filter',
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: DOCUMENT_STATUS_FILTER_OPTIONS },
		cell: ({ row }) => (
			<DocumentStatusCell
				files={row.original.poaFiles ?? []}
				overallStatus={row.original.proofOfAddressStatus}
				isExpanded={row.getIsExpanded()}
				fallbackName={tMipManage('kyc.table.columns.proof_of_address')}
			/>
		),
		showFooter: true,
		footer: () => <DocumentStatusFooter workflows={workflows} field="proofOfAddressStatus" />,
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'investmentVehicleStatus',
		headerText: tMipManage('kyc.table.columns.investment_vehicle'),
		columnLabel: tMipManage('kyc.table.columns.investment_vehicle'),
		accessorFn: (row) => row.investmentVehicleStatus,
		minSize: 200,
		enableSorting: false,
		enableGlobalFilter: false,
		showFilter: true,
		filterText: 'Filter',
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: DOCUMENT_STATUS_FILTER_OPTIONS },
		cell: ({ row }) => (
			<DocumentStatusCell
				files={row.original.vehicleFiles ?? []}
				overallStatus={row.original.investmentVehicleStatus}
				isExpanded={row.getIsExpanded()}
				fallbackName={tMipManage('kyc.table.columns.investment_vehicle')}
			/>
		),
		showFooter: true,
		footer: () => (
			<DocumentStatusFooter workflows={workflows} field="investmentVehicleStatus" />
		),
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'formsStatus',
		headerText: tMipManage('kyc.table.columns.forms'),
		columnLabel: tMipManage('kyc.table.columns.forms'),
		accessorFn: (row) => row.formsStatus,
		minSize: 200,
		enableSorting: false,
		enableGlobalFilter: false,
		showFilter: true,
		filterText: 'Filter',
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: DOCUMENT_STATUS_FILTER_OPTIONS },
		cell: ({ row }) => (
			<DocumentStatusCell
				files={row.original.formFiles ?? []}
				overallStatus={row.original.formsStatus}
				isExpanded={row.getIsExpanded()}
				fallbackName={tMipManage('kyc.table.columns.forms')}
			/>
		),
		showFooter: true,
		footer: () => <DocumentStatusFooter workflows={workflows} field="formsStatus" />,
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'investmentType',
		headerText: tMipManage('kyc.table.columns.type'),
		columnLabel: tMipManage('kyc.table.columns.type'),
		accessorFn: (row) => row.investmentType ?? InvestmentDisplayType.STANDALONE,
		size: 110,
		enableSorting: true,
		enableGlobalFilter: false,
		showFilter: true,
		filterText: 'Filter',
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: INVESTMENT_TYPE_FILTER_OPTIONS },
		cell: ({ row }) => {
			const displayType = getInvestmentDisplayType(row.original.investmentType);
			const label = getInvestmentDisplayTypeLabel(displayType);
			const color = getInvestmentDisplayTypeBadgeColor(displayType);
			if (!row.original.investmentType) {
				return <StatusBadge text={label} color={color} />;
			}
			const investorName =
				`${row.original.investor.firstName} ${row.original.investor.lastName}`.trim();
			return (
				<Tooltip label={tMipManage('kyc.table.go_to_investment')} withArrow>
					<div
						onClick={(e) => {
							e.stopPropagation();
							onInvestmentClick?.(investorName);
						}}
						style={{ cursor: 'pointer' }}
					>
						<StatusBadge text={label} color={color} />
					</div>
				</Tooltip>
			);
		},
		showFooter: true,
		footer: () => {
			const typeEntries = INVESTMENT_DISPLAY_TYPE_ORDER.map((type) => ({
				type,
				count: getInvestmentTypeCount(workflows, type),
				color: getInvestmentDisplayTypeColor(type),
				label: getInvestmentDisplayTypeLabel(type),
			})).filter((e) => e.count > 0);

			if (!typeEntries.length) {
				return null;
			}

			return (
				<Flex h="100%" w="100%" align="center">
					<HoverCard position="bottom" withinPortal closeDelay={0}>
						<HoverCard.Target>
							<Flex align="center" gap={6} wrap="wrap" style={{ cursor: 'default' }}>
								{typeEntries.map(({ type, count, color }) => (
									<Flex key={type} align="center" gap={4}>
										<StatusDotIcon color={color} />
										<Text size="sm" fw={600}>
											{count}
										</Text>
									</Flex>
								))}
							</Flex>
						</HoverCard.Target>
						<HoverCard.Dropdown p="0.5rem">
							<Flex gap="0.75rem">
								{typeEntries.map(({ type, count, color, label }) => (
									<Flex key={type} align="start" direction="column">
										<Text fw={600} size="sm">
											{count}
										</Text>
										<Flex align="center" gap={4}>
											<StatusDotIcon color={color} />
											<Text size="xs">{label}</Text>
										</Flex>
									</Flex>
								))}
							</Flex>
						</HoverCard.Dropdown>
					</HoverCard>
				</Flex>
			);
		},
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'status',
		headerText: tMipManage('kyc.table.columns.kyc_status'),
		columnLabel: tMipManage('kyc.table.columns.kyc_status'),
		accessorFn: (row) => row.status,
		size: 130,
		enableSorting: true,
		enableGlobalFilter: false,
		showFilter: true,
		filterText: 'Filter status',
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: { data: KYC_STATUS_FILTER_OPTIONS },
		cell: ({ row }) => <KycOverallStatusBadge status={row.original.status} />,
		showFooter: true,
		footer: () => {
			const kycEntries = KYC_STATUS_ORDER.map((status) => ({
				status,
				count: getKycStatusCount(workflows, status),
				color: getKycStatusDotColor(status),
				label: getKycStatusLabel(status),
			})).filter((e) => e.count > 0);

			if (!kycEntries.length) {
				return null;
			}

			return (
				<Flex h="100%" w="100%" align="center">
					<HoverCard position="bottom" withinPortal closeDelay={0}>
						<HoverCard.Target>
							<Flex align="center" gap={6} wrap="wrap" style={{ cursor: 'default' }}>
								{kycEntries.map(({ status, count, color }) => (
									<Flex key={status} align="center" gap={4}>
										<StatusDotIcon color={color} />
										<Text size="sm" fw={600}>
											{count}
										</Text>
									</Flex>
								))}
							</Flex>
						</HoverCard.Target>
						<HoverCard.Dropdown p="0.5rem">
							<Flex gap="0.75rem">
								{kycEntries.map(({ status, count, color, label }) => (
									<Flex key={status} align="start" direction="column">
										<Text fw={600} size="sm">
											{count}
										</Text>
										<Flex align="center" gap={4}>
											<StatusDotIcon color={color} />
											<Text size="xs">{label}</Text>
										</Flex>
									</Flex>
								))}
							</Flex>
						</HoverCard.Dropdown>
					</HoverCard>
				</Flex>
			);
		},
	}),
	createTextColumn<GetKYCWorkflowRowRes>({
		id: 'createdAt',
		headerText: tMipManage('kyc.table.columns.created'),
		columnLabel: tMipManage('kyc.table.columns.created'),
		accessorFn: (row) => row.createdAt,
		size: 100,
		enableSorting: true,
		enableGlobalFilter: false,
		cell: ({ row }) => (
			<Text size="sm">
				{row.original.createdAt ? formatStringDateToUI(row.original.createdAt) : LONG_DASH}
			</Text>
		),
		showFooter: true,
		footer: () => (
			<Flex direction="column" justify="space-between" h="100%" w="100%">
				<Text size="xs" c="dark.0">
					{tMipManage('payments.footer.unique_count')}
				</Text>
				<Text size="sm" fw={600}>
					{workflows.length}
				</Text>
			</Flex>
		),
	}),
	{
		cell: ({ row }: { row: { original: GetKYCWorkflowRowRes } }) => {
			const options: TableRowActionOption[] = [
				...(onKycClick
					? [
							{
								label: tMipManage('investments.actions.kyc_requirements'),
								onClick: () => onKycClick(row.original.id),
							},
						]
					: []),
				...(onInvestorDataClick
					? [
							{
								label: tMipManage('investments.actions.investor_data_requirements'),
								onClick: () => onInvestorDataClick(row.original.id),
							},
						]
					: []),
				...(onTaxComplianceClick
					? [
							{
								label: tMipManage('investments.actions.tax_and_compliance'),
								onClick: () => onTaxComplianceClick(row.original.id),
							},
						]
					: []),
				...(!row.original.investmentWorkflowId && onWithdrawStandalone
					? [
							{
								label: tMipManage('kyc.manage.withdraw_standalone'),
								onClick: () => onWithdrawStandalone(row.original.id),
							},
						]
					: []),
			];
			if (!options.length) {
				return null;
			}
			return (
				<div onClick={(e) => e.stopPropagation()}>
					<TableRowActionsMenu options={options} />
				</div>
			);
		},
		header: '',
		id: 'row-actions',
		enableGlobalFilter: false,
		enableColumnFilter: false,
		enableHiding: false,
		showFilter: false,
		minSize: 40,
		size: 40,
	},
];
